import { get, post, downloadFile } from './api_helper';
import * as Utils from '../Utils';
import { downloadClientInvoicePdf } from './invoice_pdf_client';

const getAuthToken = () => localStorage.getItem('adminToken');

const formatInr = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatInvoiceCurrency = formatInr;

export const isInvoiceIssued = (order) => Boolean(order?.invoiceNumber);

export const canDownloadInvoice = (order) => {
  if (!order) return false;
  if (order.invoicePdfUrl) return true;
  if (order.invoiceNumber) return true;
  if (order.payment === true) return true;
  if (['Completed', 'Paid'].includes(order.paymentStatus)) return true;
  if (order.status === 'Delivered') return true;
  return false;
};

export const canIssueInvoice = (order) => {
  if (!order || order.invoiceNumber) return false;
  return canDownloadInvoice(order);
};

export const encodeInvoiceNumberForPath = (invoiceNumber) =>
  encodeURIComponent(String(invoiceNumber || ''));

export const invoicePdfFilename = (invoiceNumber, orderId, orderNumber) => {
  if (orderNumber) {
    return `invoice-${String(orderNumber).replace(/\//g, '-')}.pdf`;
  }
  const base = String(invoiceNumber || orderId || 'invoice').replace(/\//g, '-');
  return `Invoice-${base}.pdf`;
};

/** Admin invoice GET paths (PR #72 — primary first). */
export const getAdminInvoiceDownloadPaths = (orderId) => [
  `v1/orders/admin/${orderId}/invoice`,
  `invoice/download/${orderId}`,
];

export const parseInvoiceContentDispositionFilename = (disposition, fallbackFilename) => {
  if (!disposition) return fallbackFilename;
  const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
  if (!match) return fallbackFilename;
  try {
    return decodeURIComponent(match[1].replace(/["']/g, '').trim());
  } catch {
    return match[1].replace(/["']/g, '').trim() || fallbackFilename;
  }
};

export const listAdminInvoices = (params = {}) => {
  const query = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      query[key] = value;
    }
  });
  return get('invoice/admin/list', query);
};

export const exportAdminInvoicesCsv = (params = {}) => {
  const query = new URLSearchParams();
  if (params.from) query.append('from', params.from);
  if (params.to) query.append('to', params.to);
  const qs = query.toString();
  const url = qs ? `invoice/admin/export?${qs}` : 'invoice/admin/export';
  const rangeLabel = `${params.from || 'all'}-${params.to || 'all'}`;
  return downloadFile(url, {
    accept: 'text/csv,application/octet-stream,*/*',
    filename: `careveli-invoices-${rangeLabel}.csv`,
    token: params.token,
  });
};

export const isPdfBlob = (contentType, blob) => {
  const type = (contentType || blob?.type || '').toLowerCase();
  return type.includes('pdf') || type.includes('octet-stream');
};

const savePdfBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
  return { filename, blob, source: 'server' };
};

const parseInvoiceErrorResponse = async (response) => {
  let message = `Download failed (${response.status})`;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      const err = await response.json();
      if (err?.message) message = err.message;
    } catch {
      /* ignore */
    }
  }
  const error = new Error(message);
  error.status = response.status;
  return error;
};

/**
 * GET admin invoice PDF — Bearer auth, blob body, filename from Content-Disposition.
 * @returns {{ blob: Blob, filename: string }}
 */
export const fetchInvoicePdfFromUrl = async (url, token, options = {}) => {
  const fallbackFilename = invoicePdfFilename(
    options.invoiceNumber,
    options.orderId,
    options.orderNumber
  );

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/pdf,application/octet-stream,*/*',
      Authorization: token ? `Bearer ${token}` : '',
    },
    redirect: 'follow',
  });

  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    throw await parseInvoiceErrorResponse(response);
  }

  if (contentType.includes('application/json')) {
    const err = await response.json();
    const error = new Error(err?.message || 'Server returned JSON instead of PDF');
    error.status = response.status || 400;
    throw error;
  }

  const blob = await response.blob();
  if (!isPdfBlob(contentType, blob)) {
    const error = new Error('Response was not a PDF file');
    error.status = 502;
    throw error;
  }

  const disposition = response.headers.get('content-disposition') || '';
  const filename = parseInvoiceContentDispositionFilename(disposition, fallbackFilename);
  return { blob, filename };
};

/**
 * Try contract admin invoice endpoints until one returns PDF or a non-404 error.
 */
export const downloadAdminInvoiceFromServer = async (orderId, options = {}) => {
  const token = options.token || getAuthToken();
  const paths = getAdminInvoiceDownloadPaths(orderId);
  let lastNotFound;

  for (const path of paths) {
    try {
      const { blob, filename } = await fetchInvoicePdfFromUrl(`${Utils.API_URL}${path}`, token, {
        ...options,
        orderId,
      });
      return savePdfBlob(blob, filename);
    } catch (error) {
      if (error.status === 404) {
        lastNotFound = error;
        continue;
      }
      throw error;
    }
  }

  throw lastNotFound || new Error('Invoice not found');
};

/**
 * Download invoice PDF for an order.
 * Server PDF is preferred; client PDF only when all admin endpoints return 404.
 */
export const downloadInvoiceByOrderId = async (orderId, options = {}) => {
  try {
    return await downloadAdminInvoiceFromServer(orderId, options);
  } catch (serverError) {
    if (serverError.status !== 404) {
      throw serverError;
    }
    const order = options.order;
    if (!order?.items?.length) {
      throw serverError;
    }
    const clientResult = downloadClientInvoicePdf(order);
    return {
      ...clientResult,
      fallback: true,
      serverError: serverError.message,
    };
  }
};

export const downloadInvoiceByNumber = (invoiceNumber, options = {}) => {
  const encoded = encodeInvoiceNumberForPath(invoiceNumber);
  return downloadFile(`invoice/admin/by-number/${encoded}`, {
    accept: 'application/pdf,application/octet-stream,*/*',
    filename: invoicePdfFilename(invoiceNumber),
    token: options.token,
  });
};

export const issueInvoiceForOrder = (orderId, token) => {
  return post(`invoice/admin/issue/${orderId}`, { token });
};

export const normalizeInvoiceList = (res) => {
  const candidates = [
    res?.data?.invoices,
    res?.data?.list,
    res?.data,
    res?.invoices,
  ];
  const firstArray = candidates.find((item) => Array.isArray(item));
  return firstArray || [];
};

export const normalizeInvoicePagination = (res) => {
  const p = res?.data?.pagination || res?.pagination;
  if (!p) return null;
  return {
    page: Number(p.page) || 1,
    pages: Number(p.pages) || Number(p.totalPages) || 1,
    total: Number(p.total) || 0,
    limit: Number(p.limit) || 20,
  };
};

export const getInvoiceBuyerName = (row) =>
  row?.buyerSnapshot?.name ||
  row?.customerName ||
  [row?.shippingAddress?.firstName, row?.shippingAddress?.lastName].filter(Boolean).join(' ') ||
  '—';

export const getGstBreakupDisplay = (row) => {
  const g = row?.gstBreakup || {};
  return {
    taxable: formatInr(g.taxableValue),
    cgst: formatInr(g.cgst),
    sgst: formatInr(g.sgst),
    igst: formatInr(g.igst),
    grand: formatInr(g.grandTotal ?? row?.total ?? row?.amount),
    supplyType: g.supplyType || '—',
  };
};
